import React, { useState} from 'react';
import Button from "react-bootstrap/Button";
import Form from 'react-bootstrap/Form';
import { useForm } from "react-hook-form";
import {createElementFromHTML, validateEmail} from "../../utils/utilsHelpers";
import Modal from "react-bootstrap/Modal";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { JSON_HEADERS, EMAIL_PATTERN, PASSWORD_PATTERN } from "../../constants/constants";
import {objectMap} from "../../../javascripts/utils";

// https://github.com/react-hook-form/react-hook-form/tree/master/examples

function RegistrationForm(props) {

    let {
        token,
        gdpr,
        cta,
        hiddenTags={},
        modalHeader,
        modalSubheader,
        allowSkip,
        passwordRule,
        showPasswordField=true
    } = props;

    // Also in props: onUserCreate, onClose

    const { register, handleSubmit, clearErrors, setError, setValue, formState: { errors, isValid, isSubmitting }} = useForm({ mode: 'onBlur'});
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [submitCta, setSubmitCta] = useState(cta);
    const { executeRecaptcha } = useGoogleReCaptcha();

    // console.log(formState.errors); // debugging: remove : { errors } from useForm above and replace all 'errors' below with 'formState.errors'

    const renderModalHeader = () => {
        return (
            <Modal.Header className={'border-bottom-0 pt-4 pb-2 justify-content-center text-center'}>
                <Modal.Title className={`fs-4 ${modalSubheader && 'mb-0'}`}>
                    {modalHeader}
                </Modal.Title>
            </Modal.Header>
            )
    }

    const renderPasswordField = () => {
        if(showPasswordField) {
            return (
                <Form.Group className="mb-3 text-start">
                    <Form.Control
                        type="password"
                        id="user_password"
                        placeholder="Choose password"
                        {...register("user.password", {
                            required: 'This field is required',
                            pattern: {
                                value: PASSWORD_PATTERN,
                                message: passwordRule
                            },
                        })} />
                    {errors.user?.password && <Form.Text className="text-danger">{errors.user.password.message}</Form.Text>}
                </Form.Group>
            )
        }
    }

    const renderEmailField = () => {
        return (
            <Form.Group className="mb-3 text-start">
                <Form.Control
                    type="email"
                    id="user_email"
                    placeholder="Company email"
                    onKeyUp={(e) => validateEmail(e.target.value, 'user.email', isEmailValid, setIsEmailValid, setEmailError, setError, clearErrors)}
                    {...register("user.email", {
                        required: 'This field is required',
                        pattern: {
                            value: EMAIL_PATTERN,
                            message: "Please enter a valid email address"
                        },
                        validate: () => isEmailValid || emailError
                    })} />
                {errors.user?.email && <Form.Text className="text-danger">{errors.user.email.message}</Form.Text>}
            </Form.Group>
        )
    }


    const renderGdpr = () => {
        return (
            <Form.Check type='checkbox' id='gdpr_checkbox' className={'form-check-primary secondary-links'}>
                <Form.Check.Input type='checkbox' isValid {...register("gdpr_checkbox", { required: 'This field is required' })} />
                <Form.Check.Label className={'content-styling text-dark font-12 text-start'}>{createElementFromHTML(gdpr)}
                    {errors.gdpr_checkbox && <Form.Text className="text-danger fw-normal">{errors.gdpr_checkbox.message}</Form.Text>}
                </Form.Check.Label>
            </Form.Check>
        )
    }

    const renderFormFields = () => {
        return (
            <>
                {renderEmailField()}
                {renderPasswordField()}
                {gdpr?.length && renderGdpr()}
            </>
        )
    }

    const renderHiddenFields = () => {
        let hiddenInputs = hiddenTags && objectMap(hiddenTags, (value, name) => {
            return <input type='hidden' id={name} key={name} value={value} {...register(name)} />
        });

        return (
            <>
                <input type='hidden' id='authenticity_token' key='authenticity_token' value={token} {...register('authenticity_token')} />
                { hiddenInputs }
            </>
        )
    }

    const onRegister = async(data) => {
        if(!isEmailValid) return;
        let captchaToken = "";
        try {
            captchaToken = await executeRecaptcha('send_report');
        } catch (error) {
            console.error('Error during recaptcha verification:', error);
        }
        data.user.email = data.user.email.trim();
        setSubmitCta('Submitting...');
        const payload = {...data, "g-recaptcha-response-data": { "send_report": captchaToken }};

        await fetch('/users', {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify(payload)
        }).then(function(response) {
            return response.json();
        }).then(function(jsonResponse) {
            if(jsonResponse.success) {
                // VWO goal conversion code
                window._vis_opt_queue = window._vis_opt_queue || [];
                window._vis_opt_queue.push(function() {_vis_opt_goal_conversion(202);});
                const updatedUser = {...jsonResponse.user, ...data.user}; // include user fields from jsonResponse
                props.onUserCreate({...jsonResponse, ...data, user: updatedUser});
            } else if(jsonResponse.errors?.email) {
                setError('user.email', { type: 'manual', message: jsonResponse.errors.email });
                setEmailError(jsonResponse.errors.email);
                setIsEmailValid(false);
            }
        });
        setSubmitCta(cta);
    }

    return (
        <>
            {renderModalHeader()}
            <Modal.Body className={`text-secondary text-center  px-1 px-sm-4 mx-1 ${modalSubheader && 'pt-0'}`}>
                <div className={'mb-4 secondary-links fs-6 text-center'}>{modalSubheader}</div>
                <Form
                    noValidate={true}
                    acceptCharset='UTF-8'
                    onSubmit={handleSubmit(onRegister)}
                >
                    {renderHiddenFields()}
                    {renderFormFields()}
                    <Button variant="primary" type="submit" className={'btn-block btn-hover-cursor my-3'} disabled={isSubmitting}>
                        {submitCta}
                    </Button>
                    {allowSkip && <a className={'text-blue-link hover-blue-hover'} onClick={props.onClose}>Skip for now</a>}
                </Form>
            </Modal.Body>
        </>
    )
}

RegistrationForm.displayName = 'RegistrationForm';

export default RegistrationForm;